/**
 * batch-65 inkoop-kosten (FO-483) — pure logica voor toerekenen en doorbelasten.
 *
 *  1. Rekenwerk van een doorbelasting (spec 183 §3.4): inkoopprijs x opslag of
 *     een vaste verkoopprijs, deelbedragen (3 van 10 stuks, T5) en creditregels.
 *     Alles in integer centen; de opslag als basispunten van de fractie
 *     (0.10 -> 1000). Afronden gebeurt één keer, aan het eind (half van nul af).
 *  2. De doorbelast-status van een regel (NIET / TE_DOORBELASTEN / DOORBELAST).
 *  3. De toerekening-voorstellen (spec 183 §3.2): uit de inkooporder, uit de
 *     referentie op de factuur en uit het geheugen leverancier x omschrijving.
 *     Alleen VOORSTELLEN: er wordt nooit automatisch gekoppeld.
 *
 * DB-vrij en unit-getest (`__tests__/inkoop-doorbelasting.test.ts`).
 */
import {
  TOEREKENING_VOORSTEL_BRON,
  TOEREKENING_VOORSTEL_BRON_LABELS,
  type InkoopDoorbelastStatus,
  type KlantToewijzingDoeltype,
  type ToerekeningVoorstelBron,
  type ToerekeningVoorstelDTO,
} from '@streep/types'

// ─── Afronding ───────────────────────────────────────────────────────────────

/** Half van nul af (commercieel), ook voor negatieve bedragen. */
export function rondHalfVanNulAf(x: number): number {
  if (!Number.isFinite(x)) return 0
  const r = Math.sign(x) * Math.round(Math.abs(x))
  return r === 0 ? 0 : r
}

/** Een aantal (max. 2 decimalen) als integer honderdsten. */
export function aantalNaarHonderdsten(aantal: number): number {
  return rondHalfVanNulAf(aantal * 100)
}

/**
 * Regeltotaal in centen van aantal x prijs per stuk, zoals een doorbelaste
 * verkoopregel het rekent (integer honderdsten x centen, één afronding, half van
 * nul af). EEN bron voor de factuur-builder, het facturatie-overzicht en de
 * reconciliatie (review-fix: 2,5 x 13,57 = 33,93 aan alle kanten, niet 33,92 door
 * een drijvende-kommafout, wat de controle "overzicht = factuur" liet falen).
 */
export function doorbelastRegelTotaalCenten(aantal: number, prijsPerStuk: number): number {
  return rondHalfVanNulAf((aantalNaarHonderdsten(aantal) * rondHalfVanNulAf(prijsPerStuk * 100)) / 100)
}

/**
 * Hoeveel er MEER is doorbelast dan toegerekend, in centen (0 = in orde). Het
 * teken van de toerekening is leidend (creditregels zijn negatief), zoals in
 * `doorbelastRestCenten`: 0 doorbelast tegenover 10,00 toegerekend is geen
 * overschot (review ronde 2).
 */
export function overschotCenten(doorbelastCenten: number, toegerekendCenten: number): number {
  if (toegerekendCenten > 0 || (toegerekendCenten === 0 && doorbelastCenten >= 0)) {
    return Math.max(0, doorbelastCenten - toegerekendCenten)
  }
  return Math.max(0, toegerekendCenten - doorbelastCenten)
}

/** Opslag-fractie (0.1 = 10%) als basispunten van 1/10000 (0.1 -> 1000). */
export function opslagNaarBasispunten(fractie: number | string): number {
  const n = typeof fractie === 'string' ? Number(fractie.replace(',', '.')) : fractie
  return rondHalfVanNulAf(n * 10000)
}

// ─── Doorbelasting berekenen ─────────────────────────────────────────────────

export interface DoorbelastBerekeningInput {
  /** Regelbedrag (excl. btw) van de inkoopregel, in centen. */
  regelTotaalCenten: number
  /** Aantal op de inkoopregel (0 of leeg = 1 stuk voor het regelbedrag). */
  regelAantal: number
  /** Nog te doorbelasten inkoop van dit doel, in centen. */
  restInkoopCenten: number
  /** Nog te doorbelasten aantal van dit doel. */
  restAantal: number
  /** Het aantal dat nu wordt doorbelast. */
  aantal: number
  prijsModus: 'OPSLAG' | 'VAST'
  /** Fractie (0.1 = 10%), bij OPSLAG. */
  opslagPct?: number | null
  /** Euro per stuk, bij VAST. */
  vastePrijsPerStuk?: number | null
}

export interface DoorbelastBerekening {
  /** Het doorbelaste deel van de inkoopkosten, in centen. */
  inkoopBedragCenten: number
  verkoopPrijsPerStukCenten: number
  verkoopBedragCenten: number
  /** Opslag in basispunten (1000 = 10%), of null bij een vaste prijs. */
  opslagBasispunten: number | null
}

/** Het effectieve aantal van een regel in honderdsten (0 wordt 1 stuk). */
function regelAantalHonderdsten(regelAantal: number): number {
  const h = aantalNaarHonderdsten(regelAantal)
  return h === 0 ? 100 : h
}

/**
 * Rekent één doorbelasting door. Bij het volledige restant (aantal = rest) is
 * het inkoopbedrag exact de rest (geen centverschil door afronden); bij een
 * deel is het regelbedrag x aantal / regelaantal. De verkoopprijs per stuk is
 * de inkoopprijs per stuk x (1 + opslag), op centen afgerond; het verkoop-
 * bedrag is aantal x die prijs (de conventie van een factuurregel).
 */
export function berekenDoorbelasting(i: DoorbelastBerekeningInput): DoorbelastBerekening {
  const regelH = regelAantalHonderdsten(i.regelAantal)
  const aantalH = aantalNaarHonderdsten(i.aantal)
  const restH = aantalNaarHonderdsten(i.restAantal)

  const inkoopBedragCenten =
    aantalH === restH ? i.restInkoopCenten : rondHalfVanNulAf((i.regelTotaalCenten * aantalH) / regelH)

  let verkoopPrijsPerStukCenten: number
  let opslagBasispunten: number | null = null
  if (i.prijsModus === 'OPSLAG') {
    opslagBasispunten = opslagNaarBasispunten(i.opslagPct ?? 0)
    // (regelTotaal / regelAantal) x (1 + opslag), in centen: één deling, één afronding.
    verkoopPrijsPerStukCenten = rondHalfVanNulAf(
      (i.regelTotaalCenten * (10000 + opslagBasispunten)) / (regelH * 100),
    )
  } else {
    verkoopPrijsPerStukCenten = rondHalfVanNulAf((i.vastePrijsPerStuk ?? 0) * 100)
  }
  const verkoopBedragCenten = rondHalfVanNulAf((aantalH * verkoopPrijsPerStukCenten) / 100)
  return { inkoopBedragCenten, verkoopPrijsPerStukCenten, verkoopBedragCenten, opslagBasispunten }
}

/** Inkoopprijs per stuk (euro, afgerond op centen) van een regel. */
export function inkoopPrijsPerStuk(regelTotaalCenten: number, regelAantal: number): number {
  return rondHalfVanNulAf((regelTotaalCenten * 100) / regelAantalHonderdsten(regelAantal)) / 100
}

/** Het aantal-equivalent (2 decimalen) van een deelbedrag van de regel. */
export function aantalVoorBedrag(regelTotaalCenten: number, regelAantal: number, bedragCenten: number): number {
  if (regelTotaalCenten === 0) return 0
  return rondHalfVanNulAf((regelAantalHonderdsten(regelAantal) * bedragCenten) / regelTotaalCenten) / 100
}

/** Rest in centen: toegerekend - doorbelast, nooit voorbij nul (teken volgt de toerekening). */
export function doorbelastRestCenten(toegerekendCenten: number, doorbelastCenten: number): number {
  const rest = toegerekendCenten - doorbelastCenten
  if (toegerekendCenten >= 0) return Math.max(0, rest)
  return Math.min(0, rest)
}

/**
 * Validatie van het aantal in de doorbelast-dialoog. null = geldig; anders een
 * melding voor het veld `aantal`.
 */
export function valideerDoorbelastAantal(aantal: number, restAantal: number): string | null {
  const a = aantalNaarHonderdsten(aantal)
  const r = aantalNaarHonderdsten(restAantal)
  if (a === 0) return 'Aantal mag niet 0 zijn.'
  if (r === 0) return 'Er staat niets meer te doorbelasten voor dit doel.'
  if (Math.sign(a) !== Math.sign(r)) {
    return r < 0
      ? 'Dit is een creditregel: het aantal moet negatief zijn.'
      : 'Het aantal moet positief zijn.'
  }
  if (Math.abs(a) > Math.abs(r)) {
    return `Er staat nog ${formatAantal(r / 100)} te doorbelasten.`
  }
  return null
}

function formatAantal(n: number): string {
  return new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 }).format(n)
}

// ─── Status van de inkoopregel ───────────────────────────────────────────────

/**
 * De doorbelast-status van een regel:
 *  - null: geen klant-toerekening en niets doorbelast (n.v.t.);
 *  - DOORBELAST: alles wat aan klanten is toegerekend, staat op een
 *    verkoopdocument (factuur of werkbonmateriaal);
 *  - NIET: er blijft een rest over, maar die wordt bewust niet doorbelast;
 *  - TE_DOORBELASTEN: er blijft een rest over (ook na een deel-doorbelasting).
 */
export function bepaalDoorbelastStatus(i: {
  klantToegerekendCenten: number
  doorbelastCenten: number
  nietDoorbelastenReden: string | null
}): InkoopDoorbelastStatus | null {
  if (i.klantToegerekendCenten === 0) return i.doorbelastCenten !== 0 ? 'DOORBELAST' : null
  const rest = doorbelastRestCenten(i.klantToegerekendCenten, i.doorbelastCenten)
  if (rest === 0) return 'DOORBELAST'
  if (i.nietDoorbelastenReden) return 'NIET'
  return 'TE_DOORBELASTEN'
}

/** Hele dagen tussen twee momenten (ouderdom in de werkvoorraad), minimaal 0. */
export function dagenTussen(van: Date, tot: Date): number {
  return Math.max(0, Math.floor((tot.getTime() - van.getTime()) / 86_400_000))
}

// ─── Voorstellen (nooit automatisch koppelen) ────────────────────────────────

export interface ToerekeningKandidaatWerkbon {
  id: string
  nummer: string
  /** De klantreferentie/PO op de werkbon. */
  referentie: string | null
  klantId: string
  klantNaam: string
}

export interface ToerekeningKandidaatProject {
  id: string
  nummer: string
  naam: string
  klantId: string
  klantNaam: string
}

function werkbonVoorstel(
  w: ToerekeningKandidaatWerkbon,
  bron: ToerekeningVoorstelBron,
  toelichting: string,
  zekerheid: 'HOOG' | 'MIDDEL',
): ToerekeningVoorstelDTO {
  return {
    doeltype: 'WERKBON',
    clientId: null,
    werkbonId: w.id,
    projectId: null,
    doelNaam: `Werkbon ${w.nummer}`,
    klantId: w.klantId,
    klantNaam: w.klantNaam,
    bron,
    bronLabel: TOEREKENING_VOORSTEL_BRON_LABELS[bron],
    toelichting,
    zekerheid,
  }
}

function projectVoorstel(
  p: ToerekeningKandidaatProject,
  bron: ToerekeningVoorstelBron,
  toelichting: string,
  zekerheid: 'HOOG' | 'MIDDEL',
): ToerekeningVoorstelDTO {
  return {
    doeltype: 'PROJECT',
    clientId: null,
    werkbonId: null,
    projectId: p.id,
    doelNaam: `Project ${p.nummer} ${p.naam}`.trim(),
    klantId: p.klantId,
    klantNaam: p.klantNaam,
    bron,
    bronLabel: TOEREKENING_VOORSTEL_BRON_LABELS[bron],
    toelichting,
    zekerheid,
  }
}

function klantVoorstel(
  k: { id: string; naam: string },
  bron: ToerekeningVoorstelBron,
  toelichting: string,
  zekerheid: 'HOOG' | 'MIDDEL',
): ToerekeningVoorstelDTO {
  return {
    doeltype: 'KLANT',
    clientId: k.id,
    werkbonId: null,
    projectId: null,
    doelNaam: k.naam,
    klantId: k.id,
    klantNaam: k.naam,
    bron,
    bronLabel: TOEREKENING_VOORSTEL_BRON_LABELS[bron],
    toelichting,
    zekerheid,
  }
}

/**
 * Voorstel uit de inkooporder (spec 183 §3.2, T2): een order die voor een
 * werkbon is besteld (`InkoopOrder.werkbonId`) wijst naar die werkbon, en
 * daarmee naar diens klant. Zonder werkbon: geen voorstel.
 */
export function voorstelUitInkooporder(order: {
  orderNummer: string
  werkbon: ToerekeningKandidaatWerkbon | null
}): ToerekeningVoorstelDTO | null {
  if (!order.werkbon) return null
  return werkbonVoorstel(
    order.werkbon,
    'INKOOPORDER',
    `Inkooporder ${order.orderNummer} is besteld voor werkbon ${order.werkbon.nummer} (${order.werkbon.klantNaam}).`,
    'HOOG',
  )
}

/** Normaliseer een referentie-token: hoofdletters, zonder spaties/punten/streepjes. */
export function normaliseerReferentie(s: string): string {
  return s.toUpperCase().replace(/[\s._\-/]+/g, '')
}

/**
 * De bruikbare tokens uit een referentie-tekst ("Uw ref: WB2600417 / PO 4500123")
 * plus de hele tekst zelf; genormaliseerd en minimaal 4 tekens met een cijfer
 * (een losse "PO" of klantnaam zegt niets).
 */
export function referentieTokens(tekst: string | null | undefined): string[] {
  if (!tekst) return []
  const tokens = new Set<string>()
  const heel = normaliseerReferentie(tekst)
  if (heel.length >= 4 && /\d/.test(heel)) tokens.add(heel)
  for (const deel of tekst.split(/[\s,;:()|]+/)) {
    const t = normaliseerReferentie(deel)
    if (t.length >= 4 && /\d/.test(t)) tokens.add(t)
  }
  return [...tokens]
}

/**
 * Voorstel uit de referentie op de factuur (AI-extractie `klantReferentie`,
 * factuur-referentie of de regelomschrijving): een werkbon- of projectnummer
 * dat letterlijk voorkomt is HOOG; de klant-PO van een werkbon (werkbon.referentie)
 * is MIDDEL. Kandidaten komen uit een gerichte DB-lookup op dezelfde tokens.
 */
export function voorstellenUitReferentie(
  referenties: ReadonlyArray<string | null | undefined>,
  kandidaten: {
    werkbonnen: ReadonlyArray<ToerekeningKandidaatWerkbon>
    projecten: ReadonlyArray<ToerekeningKandidaatProject>
  },
): ToerekeningVoorstelDTO[] {
  const tokens = new Set(referenties.flatMap((r) => referentieTokens(r)))
  if (tokens.size === 0) return []
  const uit: ToerekeningVoorstelDTO[] = []
  for (const w of kandidaten.werkbonnen) {
    if (tokens.has(normaliseerReferentie(w.nummer))) {
      uit.push(
        werkbonVoorstel(w, 'KLANTREFERENTIE', `Werkbonnummer ${w.nummer} staat op de factuur.`, 'HOOG'),
      )
    } else if (w.referentie && tokens.has(normaliseerReferentie(w.referentie))) {
      uit.push(
        werkbonVoorstel(
          w,
          'KLANTREFERENTIE',
          `Referentie ${w.referentie} op de factuur hoort bij werkbon ${w.nummer} (${w.klantNaam}).`,
          'MIDDEL',
        ),
      )
    }
  }
  for (const p of kandidaten.projecten) {
    if (tokens.has(normaliseerReferentie(p.nummer))) {
      uit.push(projectVoorstel(p, 'KLANTREFERENTIE', `Projectnummer ${p.nummer} staat op de factuur.`, 'HOOG'))
    }
  }
  return uit
}

/**
 * Voorstel uit het geheugen "leverancier x omschrijving -> klant (en project)":
 * de vorige keuze voor dezelfde omschrijving bij dezelfde leverancier. Een
 * project gaat voor zolang het nog loopt; anders de klant.
 */
export function voorstelUitGeheugen(g: {
  klant: { id: string; naam: string }
  project: (ToerekeningKandidaatProject & { actief: boolean }) | null
  aantalKeer: number
}): ToerekeningVoorstelDTO {
  const keer = g.aantalKeer === 1 ? 'eerder' : `${g.aantalKeer} keer eerder`
  if (g.project && g.project.actief) {
    return projectVoorstel(
      g.project,
      'GEHEUGEN',
      `Deze omschrijving van deze leverancier is ${keer} aan project ${g.project.nummer} toegerekend.`,
      'MIDDEL',
    )
  }
  return klantVoorstel(
    g.klant,
    'GEHEUGEN',
    `Deze omschrijving van deze leverancier is ${keer} aan ${g.klant.naam} toegerekend.`,
    'MIDDEL',
  )
}

/** Uniek-sleutel van een voorstel-doel. */
export function toerekeningVoorstelKey(v: Pick<ToerekeningVoorstelDTO, 'doeltype' | 'clientId' | 'werkbonId' | 'projectId'>): string {
  const id = v.doeltype === 'KLANT' ? v.clientId : v.doeltype === 'WERKBON' ? v.werkbonId : v.projectId
  return `${v.doeltype}:${id ?? ''}`
}

/**
 * Voegt voorstellen samen: per doel het sterkste (bron-volgorde inkooporder >
 * referentie > geheugen), HOOG vóór MIDDEL. Doelen die de regel al heeft
 * (`alGekoppeld`, sleutels uit `toerekeningVoorstelKey`) vallen weg.
 */
export function combineerToerekeningVoorstellen(
  lijsten: ReadonlyArray<ReadonlyArray<ToerekeningVoorstelDTO | null | undefined>>,
  alGekoppeld: ReadonlySet<string> = new Set(),
): ToerekeningVoorstelDTO[] {
  const bronRang = (b: ToerekeningVoorstelBron): number => TOEREKENING_VOORSTEL_BRON.indexOf(b)
  const perKey = new Map<string, ToerekeningVoorstelDTO>()
  for (const v of lijsten.flat()) {
    if (!v) continue
    const key = toerekeningVoorstelKey(v)
    if (alGekoppeld.has(key)) continue
    const bestaand = perKey.get(key)
    if (
      !bestaand ||
      (bestaand.zekerheid === 'MIDDEL' && v.zekerheid === 'HOOG') ||
      (bestaand.zekerheid === v.zekerheid && bronRang(v.bron) < bronRang(bestaand.bron))
    ) {
      perKey.set(key, v)
    }
  }
  return [...perKey.values()].sort(
    (a, b) =>
      (a.zekerheid === b.zekerheid ? 0 : a.zekerheid === 'HOOG' ? -1 : 1) || bronRang(a.bron) - bronRang(b.bron),
  )
}

/** Het doel-id van een klant-doel. */
export function klantDoelId(d: {
  doeltype: KlantToewijzingDoeltype
  clientId?: string | null
  werkbonId?: string | null
  projectId?: string | null
}): string | null {
  return d.doeltype === 'KLANT' ? (d.clientId ?? null) : d.doeltype === 'WERKBON' ? (d.werkbonId ?? null) : (d.projectId ?? null)
}
